import React, { useRef, useState } from 'react';
import { useDialogueStore } from '../store/dialogueStore';
import { Download, Upload, FileJson, RefreshCw, Plus, Layout } from 'lucide-react';
import { saveAs } from 'file-saver';
import { ThemeToggle } from './ui/ThemeToggle';
import { layoutAlgorithms } from '../utils/graphLayouts';
import { useToast } from '../contexts/ToastContext';

const Toolbar: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);
  const { dialogueData, exportToJSON, importFromJSON, generateGraphFromDialogue, addDialogueSection, nodes, edges, setNodes } = useDialogueStore();
  const { showSuccess, showError, showInfo } = useToast();

  const handleExport = () => {
    try {
      const json = exportToJSON();
      const blob = new Blob([json], { type: 'application/json' });
      const filename = dialogueData?.file_path?.split('/').pop() || 'dialogue_export.json';
      saveAs(blob, filename);
      showSuccess('Export successful', `Saved as ${filename}`);
    } catch (error) {
      showError('Export failed', 'Failed to export dialogue data');
      console.error('Export error:', error);
    }
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const content = e.target?.result as string;
        try {
          await importFromJSON(content);
          showSuccess('Import successful', `Loaded ${file.name}`);
        } catch (error) {
          showError('Import failed', error instanceof Error ? error.message : 'Invalid dialogue file');
        }
      };
      reader.onerror = () => {
        showError('Read failed', 'Failed to read file');
      };
      reader.readAsText(file);
    }
    // Reset input so the same file can be selected again
    event.target.value = '';
  };

  const handleNewDialogue = () => {
    const newSection = {
      triggers: ['new_trigger'],
      conditions: [{
        condition: 'flag.example',
        response: 'Example response',
        commands: []
      }],
      default: {
        response: 'Default response',
        commands: []
      }
    };
    
    const key = `new_dialogue_${Date.now()}`;
    addDialogueSection(key, newSection);
    generateGraphFromDialogue();
    showInfo('New dialogue created', `Added dialogue section "${key}"`);
  };

  const loadPearlExample = () => {
    fetch('/pearl_conditional_dialogue.json')
      .then(res => res.text())
      .then(data => {
        importFromJSON(data);
      })
      .catch(() => {
        const exampleData = {
          npc_name: "Example NPC",
          dialogue_name: "Example Dialogue System",
          version: "1.0",
          description: "Example dialogue for testing",
          file_path: "/example_dialogue.json",
          dialogues: {
            greetings: {
              triggers: ["hello", "hi", "greetings"],
              conditions: [
                {
                  condition: "NOT flag.met_npc",
                  response: "Hello! I don't believe we've met before.",
                  commands: [
                    { type: "set_flag", flag: "met_npc", value: true }
                  ]
                },
                {
                  condition: "flag.met_npc",
                  response: "Good to see you again!",
                  commands: []
                }
              ],
              default: {
                response: "Hello there!",
                commands: []
              }
            }
          }
        };
        importFromJSON(JSON.stringify(exampleData));
      });
  };

  const applyLayout = (algorithm: 'hierarchical' | 'radial' | 'grid') => {
    const layoutedNodes = layoutAlgorithms[algorithm](nodes, edges);
    setNodes(layoutedNodes);
    setShowLayoutMenu(false);
    showInfo('Layout applied', `Applied ${algorithm} layout to graph`);
  };

  return (
    <div className="flex items-center justify-between px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center space-x-2">
        {/* File Operations */}
        <div className="flex items-center space-x-1 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <button
            onClick={handleNewDialogue}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
          >
            <Plus size={16} /> New
          </button>
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
          >
            <Upload size={16} /> Import
          </button>
          
          <button
            onClick={handleExport}
            disabled={!dialogueData}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
              dialogueData 
                ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600' 
                : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            <Download size={16} /> Export
          </button>
        </div>

        {/* Graph Operations */}
        <div className="flex items-center space-x-1 p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <button
            onClick={generateGraphFromDialogue}
            disabled={!dialogueData}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
              dialogueData 
                ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600' 
                : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
          >
            <RefreshCw size={16} /> Refresh
          </button>

          <div className="relative">
            <button
              onClick={() => setShowLayoutMenu(!showLayoutMenu)}
              disabled={!nodes.length}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                nodes.length 
                  ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600' 
                  : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              <Layout size={16} /> Layout
            </button>
            
            {showLayoutMenu && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                <button
                  onClick={() => applyLayout('hierarchical')}
                  className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Hierarchical
                </button>
                <button
                  onClick={() => applyLayout('radial')}
                  className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Radial
                </button>
                <button
                  onClick={() => applyLayout('grid')}
                  className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Grid
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Example */}
        <button
          onClick={loadPearlExample}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-pink-600 dark:text-pink-400 bg-pink-50 dark:bg-pink-900/20 hover:bg-pink-100 dark:hover:bg-pink-900/30 rounded transition-colors"
        >
          <FileJson size={16} /> Load Example
        </button>
      </div>

      {/* Right side controls */}
      <div className="flex items-center space-x-3">
        <ThemeToggle />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleImport}
        className="hidden"
      />
    </div>
  );
};

export default Toolbar;